import { customElement, property, query, state } from 'lit/decorators.js';
import { DashboardWidget } from './dashboard-widget';
import { DateTime } from 'luxon';
import { emptyGuid } from '../../../../webmodule-common/other/api/guid';
import { getApiFactory } from '../../../api/api-injector';
import { html } from 'lit';
import { localDateToServer, serverDateTimeToLocal } from '../../../../webmodule-common/other/datetime-converter';
import { monitor } from '../../../../webmodule-common/other/monitor';
import { PurchaseOrderApi } from '../../../api/purchase-order-api';
import { PurchaseOrderBranchView } from '../../../api/dealer-api-interface-franchisee';
import { resolveURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { resourcePurchaseOrder } from '../../../purchase-orders/ui/launcher';
import { Snippet } from '../../../../webmodule-common/other/ui/events';

import {
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../webmodule-common/components/src/webmodule-components';
import { LangDashboardBranchMonthlyDeliveryPipeline } from '../../../../localization/scripts/dealer-franchisee/dashboard/widget/branch-monthly-delivery-pipeline-lang';
import { interpolateExternal } from '../../../../localization/global';
import { getPurchaseOrderNumberFormatted } from '../../../purchase-orders/data/purchase-order-helper-functions';
import { getToolTip } from '../../../../webmodule-common/other/tooltip';
import { tlang } from '../../../../webmodule-common/other/language/lang';
import { DateRange } from './dashboard-filter';
import { getUserDisplayName } from '../../common/cache-tools';

@customElement('dashboard-branch-monthly-delivery-pipeline')
export class BranchMonthlyDeliveryPipeline extends DashboardWidget {
  purchaseOrderApi: PurchaseOrderApi = getApiFactory().purchaseOrder();
  private branchCache = new Map<string, string>();
  private authorCache = new Map<string, string>();
  @property({ type: String, attribute: false }) branchId: string = emptyGuid;
  @property({ type: String, attribute: false }) assignedToUserId: string = emptyGuid;
  @property({ attribute: false }) dateRange?: DateRange;

  @query('#delivery-pipeline-po-widget')
  protected table!: WebModuleTable;
  @state()
  private data: PurchaseOrderBranchView[] = [];

  @monitor(['branchId', 'assignedToUserId', 'dateRange'])
  refresh() {
    // @monitor runs inside update() (before render), and also fires for the
    // initial property values. On that first pass the table (@query) is not in
    // the DOM yet - it auto-fetches from its own firstUpdated - so this
    // secondary refresh only needs to reload once the table actually exists.
    this.table?.fetchEvent();
  }

  getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: () => LangDashboardBranchMonthlyDeliveryPipeline.purchaseOrderNo(),
        fieldName: 'purchaseOrderNumber',
        minWidth: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderBranchView;

          return this.getPurchaseOrderLink(
            rowItem,
            rowItem.purchaseOrderNumber == 0 ? 'N/A' : getPurchaseOrderNumberFormatted(rowItem)
          );
        }
      },
      {
        title: () => LangDashboardBranchMonthlyDeliveryPipeline.purchaseOrderTitle(),
        fieldName: 'title',
        minWidth: '120px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderBranchView;
          return this.getPurchaseOrderLink(rowItem, rowItem.title);
        }
      },
      {
        title: () => LangDashboardBranchMonthlyDeliveryPipeline.mfgRef(),
        fieldName: 'reference',
        minWidth: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderBranchView;
          return rowItem.reference == null ? '' : rowItem.reference;
        }
      },
      {
        title: () => LangDashboardBranchMonthlyDeliveryPipeline.branchName(),
        fieldName: 'branchname',
        minWidth: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderBranchView;
          const branchName = this.getBranchName(rowItem.branchId) || tlang`<unavailable>`;
          return html`${branchName}`;
        }
      },
      {
        title: () => LangDashboardBranchMonthlyDeliveryPipeline.author(),
        fieldName: 'author',
        minWidth: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderBranchView;
          return this.authorCache.get(rowItem.creationUserId) ?? tlang`<unavailable>`;
        }
      },
      {
        title: () => LangDashboardBranchMonthlyDeliveryPipeline.mfgStatus(),
        fieldName: 'supplierSystemStatus',
        minWidth: '90px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderBranchView;
          return rowItem.supplierSystemStatus == null ? '' : interpolateExternal(rowItem.supplierSystemStatus);
        }
      },
      {
        title: () => LangDashboardBranchMonthlyDeliveryPipeline.proposedDelivery(),
        fieldName: 'installationDate',
        minWidth: '100px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderBranchView;
          return rowItem.installationDate == null
            ? ''
            : serverDateTimeToLocal(rowItem.installationDate).toLocaleDateString();
        }
      },
      {
        title: () => LangDashboardBranchMonthlyDeliveryPipeline.mfgDelivery(),
        fieldName: 'supplierSystemDeliveryDate',
        minWidth: '100px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const rowItem = item as PurchaseOrderBranchView;
          return rowItem.supplierSystemDeliveryDate == null
            ? ''
            : serverDateTimeToLocal(rowItem.supplierSystemDeliveryDate).toLocaleDateString();
        }
      }
    ];
  }

  protected getHeader(): Snippet {
    return html`${LangDashboardBranchMonthlyDeliveryPipeline.deliveryPipeline()}
    ${getToolTip(LangDashboardBranchMonthlyDeliveryPipeline.deliveryPipelineTooltip())}`;
  }

  protected isNearDeadlineOrOverdue(data: PurchaseOrderBranchView): boolean {
    const installDate = data.supplierSystemDeliveryDate ?? data.installationDate;
    const installationDate = installDate ? serverDateTimeToLocal(installDate) : null;

    return installationDate != null && installationDate < DateTime.now().plus({ days: 3 }).toJSDate();
  }

  protected getBody(): Snippet {
    // Near-deadline / overdue rows are tinted by the table itself via the
    // semantic row-variant hook rather than a host CSS class.
    const getRowVariant = (item: PurchaseOrderBranchView) =>
      this.isNearDeadlineOrOverdue(item) ? 'danger' : undefined;

    return html`
      <div class="delivery-pipeline-po-widget">
        <webmodule-table
          id="delivery-pipeline-po-widget"
          fill-height
          .columns=${this.getColumns()}
          .data=${this.data}
          pageLength="15"
          .clickrows=${false}
          .elevated=${false}
          .cellBorders=${false}
          .uppercaseHeader=${false}
          .rowVariantEvent=${getRowVariant as (item: object) => string | null | undefined}
          @fetch=${(e: CustomEvent) => this.internalDataLoad(e)}
        >
        </webmodule-table>
      </div>
    `;
  }

  protected getPurchaseOrderLink(row: PurchaseOrderBranchView, value: string) {
    return html`<a
      class="purchase-order-link"
      href="${resolveURL(resourcePurchaseOrder, row.id)}"
      data-purchaseorderid="${row.id}"
      >${value}</a
    >`;
  }
  protected getBranchName = (branchId: string) => {
    return this.branchCache.get(branchId) || tlang`<unavailable>`;
  };

  private async internalDataLoad(e: CustomEvent) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const index = (e.detail?.pageIndex as number) ?? 0;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const length = (e.detail?.pageLength as number) ?? 16;

    // branchId is emptyGuid when the user has more than one branch, which the server reads as
    // "every branch this session may see" and resolves through IOwnerAccessScope. There is no
    // "not set" case to guard for here: the property is a non-optional string with a default.
    const results = await this.purchaseOrderApi.getPurchaseOrderDeliveryPipeline({
      purchaseOrderOwnerId: this.branchId,
      assignedToUserId: this.assignedToUserId ?? emptyGuid,
      pageIndex: index,
      pageSize: length,
      startDate: this.dateRange?.startDate ? localDateToServer(this.dateRange.startDate) : null,
      endDate: this.dateRange?.endDate ? localDateToServer(this.dateRange.endDate) : null
    });
    if (results) {
      this.data = results.purchaseOrdersBranchView.results;
      await this.cacheBranchAndAuthorNames(this.data);
      this.table.rowCount = results.purchaseOrdersBranchView.count;
    }
  }
  private async cacheBranchAndAuthorNames(data: PurchaseOrderBranchView[]) {
    const missingBranches = [...new Set(data.map(d => d.branchId))].filter(
      id => id && id !== emptyGuid && !this.branchCache.has(id)
    );
    if (missingBranches.length) {
      const result = await getApiFactory().franchisee().getBranches();
      for (const b of result?.branches ?? []) {
        this.branchCache.set(b.id, b.abbreviation || b.name || tlang`<unavailable>`);
      }
    }

    const missingUsers = [...new Set(data.map(d => d.creationUserId))].filter(
      id => id && id !== emptyGuid && !this.authorCache.has(id)
    );
    await Promise.all(
      missingUsers.map(async id => {
        const name = await getUserDisplayName(id);
        this.authorCache.set(id, name || tlang`<unavailable>`);
      })
    );
  }
}
